import type React from 'react';
import { type FC, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Spacing16, Spacing20 } from 'autogen/design-tokens/tokens';
import type { SvgSprite as SvgSpriteType } from 'autogen/swagger/all';
import { Container, EmblaContext, emblaHOC, Slide, useNavigation, Viewport } from 'components/emblaCarousel';
import PictureDeprecated from 'components/generic/picture/PictureDeprecated';
import Icon from 'components/Icon';
import LinkAction from 'components/shared/Routing/components/LinkAction';
import useViewPromotionIntersectionObserver from 'modules/tracking/GA4/promotionEvents/hooks/useViewPromotionIntersectionObserver';
import trackSelectPromotion from 'modules/tracking/GA4/promotionEvents/trackSelectPromotion';
import type { IResponsiveImageLinkModel } from 'shared/interfaces/responsiveImage';
import type { TypeTrackingPropertiesDetailed } from 'types/autogen/contentful';
import { breakpoint, colors, sizes } from 'variables';

export type IBrandLogos = {
    brandLogos: IResponsiveImageLinkModel[];
    promoTracking?: TypeTrackingPropertiesDetailed['fields'];
};

const Wrapper = styled.div`
    display: flex;
    margin: 10px 0;
    border-top: 1px solid ${colors.borderPrimary};
    border-bottom: 1px solid ${colors.borderPrimary};
    max-width: 100%;
    overflow: hidden;
`;

const NavButton = styled.button<{ show: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 0 0 40px;
    background: 0;
    border: 0;
    padding: 0;
    opacity: ${({ show }): number => (show ? 1 : 0)};
    transition: opacity 300ms ease;
`;

const StyledViewport = styled(Viewport)`
    flex: 1 1 auto;
`;

const StyledSlide = styled(Slide)`
    display: flex;
    justify-content: center;

    // && to prevent override from other sliders
    && {
        flex: 1 0 130px;

        ${breakpoint.up(sizes.sm)} {
            flex-basis: 210px;
        }
    }
`;

const StyledLinkAction = styled(LinkAction)`
    display: flex;
    padding: ${Spacing16} ${Spacing20};
`;

const StyledPicture = styled(PictureDeprecated)`
    display: flex;

    img {
        display: block;
        max-width: 150px;
        max-height: 20px;
        object-fit: contain;
    }
`;

const Nav: FC<React.PropsWithChildren<{ icon: SvgSpriteType }>> = ({ icon }) => {
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [, embla] = useContext(EmblaContext);
    const { prev, next } = useNavigation();
    const isArrowLeft = icon === 'ArrowLeft';

    const clickHandler = isArrowLeft ? prev : next;
    const show = isArrowLeft ? canScrollPrev : canScrollNext;

    useEffect(() => {
        function setSlidesToScroll(): void {
            embla?.reInit({ slidesToScroll: breakpoint.min(sizes.md) ? 3 : 1 });
        }

        function setCanScroll(): void {
            if (embla) {
                setCanScrollPrev(embla.canScrollPrev());
                setCanScrollNext(embla.canScrollNext());
            }
        }

        if (embla) {
            setSlidesToScroll();
            setCanScroll();
            embla.on('resize', setSlidesToScroll);
            embla.on('settle', setCanScroll);
        }

        return (): void => {
            if (embla) {
                embla.off('resize', setSlidesToScroll);
                embla.off('settle', setCanScroll);
            }
        };
    });

    return (
        <NavButton onClick={clickHandler} show={show} type="button" aria-hidden tabIndex={-1}>
            {icon === 'ArrowLeft' ? <Icon type="ChevronLeft" size={16} /> : <Icon type="ChevronRight" size={16} />}
        </NavButton>
    );
};

const BrandSliderItem = ({
    brandLogo,
    promoTracking
}: {
    brandLogo: IResponsiveImageLinkModel;
    promoTracking?: TypeTrackingPropertiesDetailed['fields'];
}): JSX.Element | null => {
    const { image, link } = brandLogo;
    const trackView = useViewPromotionIntersectionObserver();
    const ref = useRef(null);

    if (!image) return null;

    const baseTrackingData = promoTracking && {
        creative_name: promoTracking.creative_name,
        creative_slot: promoTracking.creative_slot || 'Brand slider',
        promotion_id: promoTracking.promotion_id,
        promotion_name: promoTracking.promotion_name,
        items: [{ item_name: image.altText || 'None' }]
    };

    // view tracking
    if (baseTrackingData) {
        trackView({
            elementRef: ref,
            trackingType: 'EnhancedEcommerceTrackingEvent',
            ecommerce: {
                $type: 'EcommercePromotionImpression',
                ...baseTrackingData
            }
        });
    }

    // click tracking
    function handleClickTracking() {
        if (baseTrackingData && link) {
            trackSelectPromotion({
                ecommerce: { $type: 'EcommercePromotionClick', ...baseTrackingData },
                trackingType: 'EnhancedEcommerceTrackingEvent'
            });
        }
    }

    const picture = <StyledPicture {...image} lazyLoad ref={ref} />;

    return (
        <StyledSlide>
            {link ? (
                <StyledLinkAction action={link.linkAction} onClick={handleClickTracking}>
                    {picture}
                </StyledLinkAction>
            ) : (
                picture
            )}
        </StyledSlide>
    );
};

const BrandSlider = ({ brandLogos, promoTracking }: IBrandLogos): JSX.Element => (
    <Wrapper>
        <Nav icon="ArrowLeft" />
        <StyledViewport>
            <Container>
                {brandLogos.map((logo, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: no idea what to do
                    <BrandSliderItem brandLogo={logo} promoTracking={promoTracking} key={index} />
                ))}
            </Container>
        </StyledViewport>
        <Nav icon="ArrowRight" />
    </Wrapper>
);

export default emblaHOC({ align: 'start', containScroll: 'trimSnaps' })(BrandSlider);
